// @ts-nocheck
import React from 'react';
import { CardContainer } from './CardContainer';
import { SectionHeader } from './SectionHeader';
import { StatusPill } from '../design-system/StatusPill';
import { InfoPill } from '../design-system/InfoPill';
import { FinancialTable, type FinancialTableRow } from './FinancialTable';
import { colors, spacing } from '../design-system/tokens';
import { FinancialFormatter } from './FinancialFormatting';

// Percent icon SVG (matching design system style)
const PercentIcon: React.FC = () => (
  <svg 
    width="18" 
    height="24" 
    viewBox="0 0 18 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M4.5 9C6.433 9 8 7.433 8 5.5S6.433 2 4.5 2S1 3.567 1 5.5S2.567 9 4.5 9ZM4.5 9L13.5 2M13.5 15C15.433 15 17 13.433 17 11.5S15.433 8 13.5 8S10 9.567 10 11.5S11.567 15 13.5 15Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface ShipmentCommissionData {
  shipmentNumber: string;
  commissionAmount: number;
  expenses: number;
  status: 'invoiced' | 'pending' | 'paid';
}

export interface CommissionMarginData {
  commissionRate: number; // As percentage (e.g., 2.0 for 2.0%)
  commissionShare: number; // As percentage (e.g., 50.0 for 50.0%)
  shipments: ShipmentCommissionData[];
}

interface CommissionMarginCardProps {
  data: CommissionMarginData;
  contract: any; // Contract data for calculations
  formatter?: FinancialFormatter; // Optional shared formatter for alignment
  onEditExpenses?: (shipmentNumber: string) => void;
  className?: string;
}

export const CommissionMarginCard: React.FC<CommissionMarginCardProps> = ({ 
  data, 
  contract,
  formatter,
  onEditExpenses,
  className = '' 
}) => {
  // Calculate commission for a shipment based on goods
  const calculateShipmentCommission = (shipmentNumber: string): number => {
    const shipment = contract.shipments?.find((s: any) => s.shipmentNumber === shipmentNumber);
    if (!shipment) return 0;

    let totalCommission = 0;

    // Calculate commission for each container's goods
    shipment.containers?.forEach((container: any) => {
      container.goods?.forEach((goodsItem: any) => {
        const details = goodsItem.details;
        const price = parseFloat(details.price) || 0;
        const units = parseFloat(details.units) || 0;
        const lbsEa = parseFloat(details.lbsEa) || 0;

        // If lbsEa is specified and > 0, use per-pound calculation
        // Otherwise use per-unit calculation
        let goodsCommission = 0;
        if (lbsEa > 0) {
          // [Commission rate] * [Price] * [Units] * [Lbs per Unit]
          goodsCommission = (data.commissionRate / 100) * price * units * lbsEa;
        } else {
          // [Commission rate] * [Price] * [Units] (for per-unit pricing)
          goodsCommission = (data.commissionRate / 100) * price * units;
        }

        totalCommission += goodsCommission;
      });
    });

    // Apply commission share
    return totalCommission * (data.commissionShare / 100);
  };

  // Calculate total commission across all shipments
  const calculateTotalCommission = (): number => {
    return data.shipments.reduce((total, shipment) => {
      const commission = calculateShipmentCommission(shipment.shipmentNumber);
      const netCommission = commission - Math.abs(shipment.expenses);
      return total + netCommission;
    }, 0);
  };

  const formatCurrency = (amount: number): string => {
    return `$ ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate the longest number in the card to determine padding
  const getLongestNumberLength = (): number => {
    const numbers: number[] = [];
    
    // Add all monetary values that will be displayed
    data.shipments.forEach(shipment => {
      const commission = calculateShipmentCommission(shipment.shipmentNumber);
      numbers.push(commission);
      numbers.push(Math.abs(shipment.expenses));
    });
    
    // Add total commission
    numbers.push(calculateTotalCommission());
    
    // Find the longest formatted number (without $ sign)
    const longestLength = Math.max(
      ...numbers.map(num => 
        num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).length
      )
    );
    
    return longestLength;
  };

  // Format currency with proper padding for dollar sign alignment
  const formatCurrencyWithPadding = (amount: number, isNegative: boolean = false): string => {
    // Use shared formatter if available, otherwise fall back to local calculation
    if (formatter) {
      return formatter.formatCurrencyWithPadding(amount, isNegative);
    }
    
    // Fallback to local formatting
    const longestLength = getLongestNumberLength();
    const formattedNumber = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const paddingSpaces = ' '.repeat(longestLength - formattedNumber.length + 1);
    
    if (isNegative) {
      return `-$${paddingSpaces}${formattedNumber}`;
    } else {
      return `$${paddingSpaces}${formattedNumber}`;
    }
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const getCommissionStatusVariant = (status: string): 'positive' | 'negative' | 'neutral' | 'warning' => {
    switch (status) {
      case 'invoiced':
      case 'paid':
        return 'positive';
      case 'pending':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getStatusText = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Build table rows for the financial table
  const buildTableRows = (): FinancialTableRow[] => {
    const rows: FinancialTableRow[] = [];

    // Commission Rate
    rows.push({
      id: 'commission-rate',
      label: 'Commission Rate',
      value: formatPercentage(data.commissionRate)
    });

    // Commission Share
    rows.push({
      id: 'commission-share',
      label: 'Commission Share',
      value: formatPercentage(data.commissionShare)
    });

    // Shipment details
    data.shipments.forEach((shipment) => {
      const calculatedCommission = calculateShipmentCommission(shipment.shipmentNumber);
      
      // Shipment Commission
      rows.push({
        id: `shipment-${shipment.shipmentNumber}-commission`,
        label: `Shipment ${shipment.shipmentNumber} Commission`,
        value: formatCurrencyWithPadding(calculatedCommission, false),
        actionComponent: (
          <StatusPill variant={getCommissionStatusVariant(shipment.status)}>
            {getStatusText(shipment.status)}
          </StatusPill>
        )
      });

      // Shipment Expenses
      rows.push({
        id: `shipment-${shipment.shipmentNumber}-expenses`,
        label: `Shipment ${shipment.shipmentNumber} Expenses`,
        value: formatCurrencyWithPadding(Math.abs(shipment.expenses), shipment.expenses !== 0),
        isNegative: shipment.expenses !== 0,
        actionComponent: (
          <InfoPill 
            variant="link"
            showIcon={true}
            onClick={() => onEditExpenses?.(shipment.shipmentNumber)}
            style={{ cursor: 'pointer' }}
          >
            Edit
          </InfoPill>
        )
      });
    });

    // Separator
    rows.push({
      id: 'separator',
      label: '',
      value: '',
      isSeparator: true
    });

    // Total Commission
    rows.push({
      id: 'total-commission',
      label: 'Total Commission',
      value: formatCurrencyWithPadding(calculateTotalCommission(), false),
      isTotal: true
    });

    return rows;
  };

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Header */}
        <SectionHeader 
          title="Commission & Margin"
          icon={<div style={{ width: '18px', height: '24px' }}><PercentIcon /></div>}
          showBottomBorder={true}
        />

        {/* Financial Table */}
        <FinancialTable rows={buildTableRows()} />
      </CardContainer>
    </div>
  );
};

export default CommissionMarginCard;